/**
 * The activity map's fixed vocabulary - the same lists the backend
 * (backend/src/common/map-activity.ts) and the database CHECK constraints hold.
 *
 * Which activity is drawn in which shape, and which stage in which colour, is
 * the tenant's choice; this file only knows how to draw each shape.
 */
export const MAP_SHAPES = [
  'CIRCLE',
  'SQUARE',
  'TRIANGLE',
  'DIAMOND',
  'STAR',
  'PENTAGON',
  'HEXAGON',
  'CROSS',
] as const;
export type MapShape = (typeof MAP_SHAPES)[number];

export const MAP_STAGES = [
  'PROPOSAL',
  'FIELD_DONE',
  'VENDOR_VERIFIED',
  'OFFICER_VERIFIED',
  'PAID',
] as const;
export type MapStage = (typeof MAP_STAGES)[number];

/** A regular polygon with `n` corners, first corner at the top, in a 24x24 box. */
function polygon(n: number, radius: number, rotation = -Math.PI / 2): string {
  const pts: string[] = [];
  for (let i = 0; i < n; i++) {
    const a = rotation + (i * 2 * Math.PI) / n;
    pts.push(`${(12 + radius * Math.cos(a)).toFixed(2)},${(12 + radius * Math.sin(a)).toFixed(2)}`);
  }
  return pts.join(' ');
}

function star(): string {
  const pts: string[] = [];
  for (let i = 0; i < 10; i++) {
    const a = -Math.PI / 2 + (i * Math.PI) / 5;
    const r = i % 2 === 0 ? 10 : 4.2;
    pts.push(`${(12 + r * Math.cos(a)).toFixed(2)},${(12 + r * Math.sin(a)).toFixed(2)}`);
  }
  return pts.join(' ');
}

/** The SVG of one marker: the activity's shape, filled in its stage's colour. */
export function markerSvg(shape: string | null | undefined, colour: string, size = 22): string {
  const stroke = 'stroke="#1F2937" stroke-width="1.4"';
  const fill = `fill="${colour}"`;
  let body: string;
  switch (shape) {
    case 'SQUARE':
      body = `<rect x="3" y="3" width="18" height="18" rx="1.5" ${fill} ${stroke}/>`;
      break;
    case 'TRIANGLE':
      body = `<polygon points="${polygon(3, 11, -Math.PI / 2)}" ${fill} ${stroke}/>`;
      break;
    case 'DIAMOND':
      body = `<polygon points="12,1.5 22.5,12 12,22.5 1.5,12" ${fill} ${stroke}/>`;
      break;
    case 'STAR':
      body = `<polygon points="${star()}" ${fill} ${stroke}/>`;
      break;
    case 'PENTAGON':
      body = `<polygon points="${polygon(5, 10.5)}" ${fill} ${stroke}/>`;
      break;
    case 'HEXAGON':
      body = `<polygon points="${polygon(6, 10.5, 0)}" ${fill} ${stroke}/>`;
      break;
    case 'CROSS':
      body = `<polygon points="8,2 16,2 16,8 22,8 22,16 16,16 16,22 8,22 8,16 2,16 2,8 8,8" ${fill} ${stroke}/>`;
      break;
    default:
      body = `<circle cx="12" cy="12" r="9.5" ${fill} ${stroke}/>`;
  }
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24">${body}</svg>`;
}
